import { AsyncPipe, NgClass } from '@angular/common';
import { Component, ElementRef, HostBinding, OnDestroy, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';

import { AutomationDirective, AutomationService } from '@frontend/sports/automation/core-feature';
import { ScrollingSizeConfig } from '@frontend/sports/common/client-config-data-access';
import { HooksWireup, OnRouteResolve } from '@frontend/sports/common/core/utils/hooks';
import { ScrollContainerService } from '@frontend/sports/common/core/utils/scroll-container';
import { ScrollProxyService } from '@frontend/sports/common/core/utils/scroll-service-proxy';
import { ModuleLoaderService } from '@frontend/sports/deferred/feature/module-loader';
import { MEDIUM_LAYOUT_BREAKPOINT } from '@frontend/sports/layout/feature';
import { Widget, WidgetLayoutResponse, WidgetLayoutTemplate, WidgetLocation, WidgetPage } from '@frontend/sports/types/components/widget';
import {
    WidgetExperimentService,
    WidgetLoaderService,
    WidgetPageService,
    WidgetRightColumnService,
    WidgetTrackingService,
    forSlot,
    widgetsToWidgetTypes,
} from '@frontend/sports/widget/core/feature';
import { WidgetSlotComponent } from '@frontend/sports/widget/feature/slot';
import { MediaQueryService } from '@frontend/vanilla/core';
import { kebabCase } from 'lodash-es';
import { Observable, ReplaySubject, Subscription, map, switchMap } from 'rxjs';

import { WidgetLayoutHook } from './widget-layout-hook';

export interface WidgetViewModel {
    top: Widget<unknown>[];
    left: Widget<unknown>[];
    center: Widget<unknown>[];
}

@HooksWireup()
@Component({
    selector: 'ms-widget-layout',
    templateUrl: './widget-layout.component.html',
    imports: [WidgetSlotComponent, AutomationDirective, NgClass, AsyncPipe],
    providers: [WidgetExperimentService],
})
export class WidgetLayoutComponent extends OnRouteResolve<WidgetLayoutResponse> implements OnDestroy, OnInit {
    @HostBinding('class') className: string;

    page: WidgetPage;
    layoutTemplate: WidgetLayoutTemplate;

    private widgetLayout$ = new ReplaySubject<WidgetLayoutResponse>(1);

    leftVisible = true;
    private scrollSubscription?: Subscription;
    private hasLeftWidgets = false;

    private widgetPageService = inject(WidgetPageService);
    private media = inject(MediaQueryService);
    private column = inject(WidgetRightColumnService);
    private elementRef = inject(ElementRef);
    private automationService = inject(AutomationService);
    private widgetTrackingService = inject(WidgetTrackingService);
    private moduleLoader = inject(ModuleLoaderService);
    private scrollContainer = inject(ScrollContainerService);
    private hideHeaderService = inject(ScrollProxyService);
    private scrollingSizeConfig = inject(ScrollingSizeConfig);
    private widgetLoaderService = inject(WidgetLoaderService);
    private hooks = (inject(WidgetLayoutHook, { optional: true }) ?? []) as WidgetLayoutHook[];
    private readonly widgetExperimentService = inject(WidgetExperimentService);

    protected widgetModel$: Observable<WidgetViewModel> = this.widgetLayout$.pipe(
        switchMap((model) => this.widgetExperimentService.filterWidgetsForExperiments$(model)),
        map((model) => {
            //on demand lazy load widgets using vanilla event strategy approach.
            const widgetTypes = widgetsToWidgetTypes(model.widgets);
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            this.moduleLoader.lazyLoadFeatures(widgetTypes!);

            const top = forSlot(model, WidgetLocation.Top);
            const left = forSlot(model, WidgetLocation.Left);
            const center = forSlot(model, WidgetLocation.Center);

            this.hasLeftWidgets = left.length > 0;

            this.widgetPageService.setActivePage({
                page: model.page,
                top,
                left,
                center,
            });

            this.column.setContent(forSlot(model, WidgetLocation.Right), model.page, this.layoutTemplate);
            this.automationService.addAttr(this.elementRef, { widgetPage: model.page, widgetPageId: model.id });

            this.setLeftVisibility();
            this.setClass(model.page);
            this.widgetTrackingService.trackWidgetsLoaded(model);
            return {
                top,
                left,
                center,
            };
        }),
    );

    constructor() {
        const route = inject(ActivatedRoute);

        super(route);

        this.media
            .observe()
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.setLeftVisibility());

        this.setClass();
    }

    ngOnInit(): void {
        if (this.scrollingSizeConfig.hideHeaderOnScroll.includes(this.page)) {
            this.scrollSubscription = this.scrollContainer.scroll.subscribe(() => {
                this.hideHeaderService.scroll();
            });
        }
    }

    ngOnDestroy(): void {
        this.scrollSubscription?.unsubscribe();
        this.widgetLoaderService.cleanWidgetsLoaded();
        this.column.resetContent();
        this.destroyHooks();
        this.widgetPageService.reset();
    }

    onRouteResolved(model: WidgetLayoutResponse): void {
        if (this.page !== model.page) {
            this.destroyHooks();
        }

        this.page = model.page;

        this.layoutTemplate = model.template;
        this.resolveHooks();
        this.widgetLoaderService.cleanWidgetsLoaded();

        this.widgetLayout$.next(model);
    }

    private setLeftVisibility(): void {
        this.leftVisible = this.media.isActive(MEDIUM_LAYOUT_BREAKPOINT) && this.hasLeftWidgets;
    }

    private setClass(page?: string): void {
        this.className = ['widget-layout', 'row', kebabCase(page)].filter(Boolean).join(' ');
    }

    private resolveHooks(): void {
        this.getHooks().forEach((hook) => hook.onResolve());
    }

    private destroyHooks(): void {
        this.getHooks().forEach((hook) => hook.onDestroy());
    }

    private getHooks(): WidgetLayoutHook[] {
        return this.hooks.filter((hook) => hook.target === this.page);
    }
}
